import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withFieldProps } from 'hooks/useFieldProps';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import { withStyles } from 'theme/withStyles';
import checkboxStyles from 'styles/checkbox';
import ErrorMsg from 'common/ErrorMsg';
import { styles as labelStyles } from 'common/FlyupLabel';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

import rootStyles from './styles/root';

const styles = (theme) => ({
  checkbox: {
    ...checkboxStyles(theme),
    marginRight: theme.spacing.unit * 3,
  },
  label: {
    ...labelStyles(theme),
    position: 'relative',
  },
  root: {
    ...rootStyles(theme),
    '& .form-group': {
      marginTop: theme.spacing.xs,
    },
    '& .help-text': {
      position: 'relative',
      top: 5,
    },
    '& label': {
      ...theme.typography.p,
      ...theme.typography.p3,
    },
  },
});

const MultiChoice = ({
  classes,
  displayOrder,
  error,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  label,
  loading,
  name,
  onChange,
  options,
  placeholder,
  readOnly,
  required,
  tooltip,
  validate,
  value,
}) => {
  const lookupOrder = displayOrder === undefined ? Object.keys(options) : displayOrder;
  const optionalLabel = !required
    ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>`
    : '';
  const labelString = `${label}${optionalLabel}`;
  const valArr = (value && value.split(',')) || [];
  return (
    <div className={classnames('form-group', { error, loading }, classes.root)}>
      <Spinner loading={loading} />
      <fieldset>
        <RawHtml type="legend" className={classes.label}>
          {labelString}
        </RawHtml>
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        <Help help={help} id={`help-${name}`} />
        {lookupOrder.map((id) => (
          <div key={`${fieldDomId}-${id}`} className="checkbox form-group">
            <input
              type="checkbox"
              name={name}
              aria-describedby={
                classnames({
                  [`help-${name}`]: help,
                  [`error-${name}`]: error,
                }) || undefined
              }
              id={`${fieldDomId}-${id}`}
              placeholder={placeholder}
              disabled={readOnly}
              checked={valArr.includes(id)}
              className={classes.checkbox}
              onChange={() => {
                onChange(
                  name,
                  valArr.includes(id)
                    ? valArr.filter((k) => k !== id).join(',')
                    : valArr.concat(id).join(','),
                );
                validate();
              }}
              value={id}
            />{' '}
            <RawHtml type="label" htmlFor={`${fieldDomId}-${id}`}>
              {options[id]}
            </RawHtml>
          </div>
        ))}
      </fieldset>

      <ErrorMsg name={name} />
    </div>
  );
};

MultiChoice.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  displayOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({}).isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const MultiChoiceStyled = withStyles(styles)(MultiChoice);

const mapStateToProps = ({ fields, resources }, { name }) => ({
  displayOrder: fields[name].lookupOrder || Object.keys(fields[name].lookupValues),
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix ?? '',
  options: fields[name].lookupValues,
});

export default withFieldProps(mapStateToProps)(MultiChoiceStyled);
